import { createSelector } from '@reduxjs/toolkit';
import { getFormSyncErrors, isDirty } from 'redux-form';
import { AdminState } from 'reduxStore/appStore';
import { selectEmailTemplateByIdByLanguage } from 'reduxStore/emailDistribution/selectors';
import { selectCurrentItemByLanguage } from 'reduxStore/page/selectors';
import { AdminFormName, AdminFormNames } from 'register/AdminFormNames';
import { LangCodeEnum } from 'shared/enums/LangCodeEnum';
import { languageConfig } from 'translations/languageConfig';

export const selectChangedLanguages = (formName: AdminFormName) => {
  return createSelector(
    (state: AdminState) => state,
    selectCurrentItemByLanguage,
    (state, currentItemByLanguage) => {
      return (Object.keys(currentItemByLanguage) as LangCodeEnum[])
        .map((language) => {
          const translationFormName = `${formName}:${language}`;
          return isDirty(translationFormName)(state) && language;
        })
        .filter((item) => !!item);
    },
  );
};

export const selectChangedEmailTemplateLanguages = (
  formName: AdminFormName,
  emailTemplateId: number,
) => {
  return createSelector(
    (state: AdminState) => state,
    selectEmailTemplateByIdByLanguage,
    (state, emailTemplateByIdByLanguage) => {
      const templateByLanguage = emailTemplateByIdByLanguage?.[emailTemplateId];
      if (!templateByLanguage) return [];

      return (Object.keys(templateByLanguage) as LangCodeEnum[])
        .map((language) => {
          const translationFormName = `${formName}:${language}`;
          return isDirty(translationFormName)(state) && language;
        })
        .filter((item): item is LangCodeEnum => Boolean(item));
    },
  );
};

export const selectEmailTemplateLanguagesWithValidationErrors = (
  formName: AdminFormName,
  emailTemplateId: number,
) =>
  createSelector(
    (state: AdminState) => state,
    selectChangedEmailTemplateLanguages(formName, emailTemplateId),
    getFormSyncErrors(AdminFormNames.PulseEmailTemplate),
    (state, changedLanguages, baseLanguageFormErrors) => {
      const languagesWithValidationErrors = changedLanguages.filter((locale) => {
        const formName = `${AdminFormNames.PulseEmailTemplateTranslation}:${locale}`;
        const formErrors = getFormSyncErrors(formName)(state);
        return Object.keys(formErrors).length > 0;
      });
      const isBaseLanguageFormDirty = isDirty(AdminFormNames.PulseEmailTemplate)(state);

      return isBaseLanguageFormDirty && Object.keys(baseLanguageFormErrors).length > 0
        ? ([languageConfig.baseLanguage, ...languagesWithValidationErrors] as LangCodeEnum[])
        : languagesWithValidationErrors;
    },
  );

export const selectEmailTemplateBaseFormHasError = createSelector(
  (state: AdminState) => state,
  getFormSyncErrors(AdminFormNames.PulseEmailTemplate),
  (state, baseLanguageFormErrors) => {
    const isBaseLanguageFormDirty = isDirty(AdminFormNames.PulseEmailTemplate)(state);

    if (isBaseLanguageFormDirty && Object.keys(baseLanguageFormErrors).length > 0) {
      return true;
    }

    return false;
  },
);

export const selectLanguagesWithValidationErrors = (formName: AdminFormName) =>
  createSelector(
    (state: AdminState) => state,
    selectChangedLanguages(formName),
    getFormSyncErrors(AdminFormNames.CreateQuestion),
    (state, changedLanguages, baseLanguageFormErrors) => {
      const languagesWithValidationErrors = changedLanguages.filter((locale) => {
        const formName = `${AdminFormNames.CreateQuestionTranslation}:${locale}`;
        const formErrors = getFormSyncErrors(formName)(state);
        return Object.keys(formErrors).length > 0;
      });
      const isBaseLanguageFormDirty = isDirty(AdminFormNames.CreateQuestion)(state);

      return isBaseLanguageFormDirty && Object.keys(baseLanguageFormErrors).length > 0
        ? ([languageConfig.baseLanguage, ...languagesWithValidationErrors] as LangCodeEnum[])
        : languagesWithValidationErrors;
    },
  );

export const selectAllChangedLanguages = (formName: AdminFormName) =>
  createSelector(
    (state: AdminState) => state,
    selectChangedLanguages(formName),
    (state, changedLanguages) => {
      const isBaseLanguageFormDirty = isDirty(AdminFormNames.CreateQuestion)(state);

      return isBaseLanguageFormDirty
        ? ([languageConfig.baseLanguage, ...changedLanguages] as LangCodeEnum[])
        : changedLanguages;
    },
  );
